import { cmsPublicContent } from "./cmscontent/cms";

export interface InsightPostMeta {
  id: number;
  title: string;
  slug: string;
}

export const INSIGHT_POST_IDS = [
  14626, 14563, 14629, 14560, 14614, 14649, 14599, 14601, 14607, 14646, 14632,
  14623, 14617, 14608, 14595, 14566, 14569, 14575, 14590, 14578, 14551, 14554,
  14530, 14529, 14526, 14523, 14505, 14512, 14494, 14491, 14470, 14467, 14458,
  14455, 14446, 14443, 14436, 14434, 14416, 14415, 14407, 14404, 14395, 14392,
  14376, 14379, 14367, 14364, 14356, 14353, 14272, 14269, 13996, 14233, 14230,
  14218, 14215, 14008, 14170, 14169, 14158, 14155, 14149, 14146, 14137, 14134,
  14128, 14127, 14104, 14106, 14098, 14097, 14086, 14085, 14076, 14073, 14065,
  14062, 14056, 14055, 14047, 14046, 14038, 14029, 14026, 14014, 14017, 14005,
  13999, 13987, 13984, 13969, 13966, 13957, 13954, 13945, 13924, 13927, 13909,
  13890, 13893, 13876, 13866, 13848, 13840,
];

export const POST_CATEGORIES: Record<number, string> = {
  14626: "Tutorials",
  14563: "General",
  14629: "Leadership",
  14560: "Technology & Infrastructure",
  14614: "General",
  14649: "Tutorials",
  14599: "Workflows",
  14601: "Project Control",
  14607: "Project Control",
  14646: "Proof & Signals",
  14632: "Leadership",
  14623: "Workflows",
  14617: "Tutorials",
  14608: "Workflows",
  14595: "AI Teams",
  14566: "AIT & Infra",
  14569: "AIT & Infra",
  14575: "Workflows",
  14590: "AI Teams",
  14578: "Tutorials",
  14551: "AIT & Infra",
  14554: "AIT & Infra",
  14530: "AIT & Infra",
  14529: "AIT & Infra",
  14526: "AIT & Infra",
  14523: "AIT & Infra",
  14505: "Workflows",
  14512: "Workflows",
  14494: "AI Teams",
  14491: "AI Teams",
  14470: "Project Control",
  14467: "Project Control",
  14458: "Workflows",
  14455: "Workflows",
  14446: "Workflows",
  14443: "Workflows",
  14436: "AI Teams",
  14434: "Workflows",
  14416: "AIT & Infra",
  14415: "AIT & Infra",
  14407: "Workflows",
  14404: "Workflows",
  14395: "AI Teams",
  14392: "AI Teams",
  14376: "AI Teams",
  14379: "AI Teams",
  14367: "Workflows",
  14364: "Workflows",
  14356: "Leadership",
  14353: "Workflows",
  14272: "Workflows",
  14269: "Tutorials",
  13996: "Project Control",
  14233: "Leadership",
  14230: "Leadership",
  14218: "Leadership",
  14215: "Leadership",
  14008: "Project Control",
  14170: "Governance",
  14169: "Governance",
  14158: "Workflows",
  14155: "Workflows",
  14149: "Governance",
  14146: "Governance",
  14137: "Proof & Signals",
  14134: "Proof & Signals",
  14128: "AIT & Infra",
  14127: "AIT & Infra",
  14104: "Workflows",
  14106: "Workflows",
  14098: "Workflows",
  14097: "AI Teams",
  14086: "Leadership",
  14085: "Leadership",
  14076: "Productivity",
  14073: "Productivity",
  14065: "Leadership",
  14062: "Leadership",
  14056: "Remote Work",
  14055: "Workflows",
  14047: "Workflows",
  14046: "Workflows",
  14038: "General",
  14029: "Project Control",
  14026: "Productivity",
  14014: "Project Control",
  14017: "Project Control",
  14005: "Project Control",
  13999: "Governance",
  13987: "Project Control",
  13984: "Project Control",
  13969: "Project Control",
  13966: "Remote Work",
  13957: "Productivity",
  13954: "Productivity",
  13945: "Productivity",
  13924: "Remote Work",
  13927: "Productivity",
  13909: "Leadership",
  13890: "Remote Work",
  13893: "General",
  13876: "General",
  13866: "General",
  13848: "General",
  13840: "General",
};

const INSIGHT_META_CACHE_KEY = "insight_post_meta_cache";

function isAsciiLetterOrDigit(character: string) {
  return (
    (character >= "a" && character <= "z") ||
    (character >= "0" && character <= "9")
  );
}

function isCombiningMark(character: string) {
  const codePoint = character.codePointAt(0) || 0;
  return codePoint >= 0x0300 && codePoint <= 0x036f;
}

function isNumericSegment(value: string) {
  if (!value) {
    return false;
  }

  for (const character of value) {
    if (character < "0" || character > "9") {
      return false;
    }
  }

  return true;
}

export function slugifyInsightTitle(title: string): string {
  const normalizedTitle = title.toLowerCase().trim().normalize("NFKD");
  let slug = "";
  let pendingSeparator = false;

  for (const character of normalizedTitle) {
    if (isCombiningMark(character)) {
      continue;
    }

    if (isAsciiLetterOrDigit(character)) {
      if (pendingSeparator && slug) {
        slug += "-";
      }
      slug += character;
      pendingSeparator = false;
      continue;
    }

    if (slug) {
      pendingSeparator = true;
    }
  }

  return slug;
}

export function extractInsightPreview(html: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const titleEl = doc.querySelector("h1, h2, h3");
  const title = titleEl?.textContent?.trim() || "";
  const paragraphs = Array.from(doc.querySelectorAll("p"))
    .map((paragraph) => paragraph.textContent?.trim() || "")
    .filter(Boolean);

  return {
    title,
    slug: slugifyInsightTitle(title),
    description: paragraphs.join(" "),
    paragraphs,
  };
}

function buildInsightPostMeta(id: number, title: string): InsightPostMeta {
  const safeTitle = title || "Untitled";

  return {
    id,
    title: safeTitle,
    slug: slugifyInsightTitle(safeTitle) || `post-${id}`,
  };
}

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function readInsightMetaCache(): InsightPostMeta[] {
  if (!canUseSessionStorage()) {
    return [];
  }

  try {
    const raw = window.sessionStorage.getItem(INSIGHT_META_CACHE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (item): item is InsightPostMeta =>
        !!item &&
        typeof item.id === "number" &&
        typeof item.title === "string" &&
        typeof item.slug === "string",
    );
  } catch {
    return [];
  }
}

export function cacheInsightPostMeta(posts: InsightPostMeta[]) {
  if (!canUseSessionStorage() || posts.length === 0) {
    return;
  }

  const byId = new Map<number, InsightPostMeta>();
  [...readInsightMetaCache(), ...posts].forEach((post) => {
    if (post.slug) {
      byId.set(post.id, post);
    }
  });

  window.sessionStorage.setItem(
    INSIGHT_META_CACHE_KEY,
    JSON.stringify(Array.from(byId.values())),
  );
}

async function fetchInsightPostMetaById(
  id: number,
  lat: string,
  log: string,
): Promise<InsightPostMeta | null> {
  const response = await cmsPublicContent({
    post_id: String(id),
    lat,
    log,
  });

  if (!response.length) {
    return null;
  }

  const preview = extractInsightPreview(response[0] || "");
  const postMeta = buildInsightPostMeta(id, preview.title);
  cacheInsightPostMeta([postMeta]);
  return postMeta;
}

export async function resolveInsightPostRouteSegment(
  routeSegment: string,
  lat: string,
  log: string,
): Promise<InsightPostMeta | null> {
  const normalizedSegment = routeSegment.trim().toLowerCase();
  if (!normalizedSegment) {
    return null;
  }

  const cachedPosts = readInsightMetaCache();
  const numericId = isNumericSegment(normalizedSegment) ? Number(normalizedSegment) : null;

  if (numericId !== null) {
    const cachedPost = cachedPosts.find((post) => post.id === numericId);
    if (cachedPost) {
      return cachedPost;
    }

    return fetchInsightPostMetaById(numericId, lat, log);
  }

  const cachedPost = cachedPosts.find((post) => post.slug === normalizedSegment);
  if (cachedPost) {
    return cachedPost;
  }

  const batchSize = 12;
  for (let index = 0; index < INSIGHT_POST_IDS.length; index += batchSize) {
    const batch = INSIGHT_POST_IDS.slice(index, index + batchSize);
    const batchPosts = (
      await Promise.all(batch.map((id) => fetchInsightPostMetaById(id, lat, log)))
    ).filter((post): post is InsightPostMeta => !!post);

    const matchedPost = batchPosts.find((post) => post.slug === normalizedSegment);
    if (matchedPost) {
      return matchedPost;
    }
  }

  return null;
}